import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout } from './auth';
import listingsService from '../../api/services/listings';
import { Prisma } from '@prisma/client';

export type listingsWithUploadsAndOpenHouses = Prisma.listingsGetPayload<{
  include: { uploads: true; openHouses: true; user: true; pricingOpinion: true };
}>;

interface State {
  listings: listingsWithUploadsAndOpenHouses[];
  loading: boolean;
  /** True once a fetch has succeeded; lets callers tell "empty" from "not asked yet". */
  loaded: boolean;
  /** The fetch whose result may still land; cleared on logout so an old session's reply is dropped. */
  activeRequestId: string | null;
  error: string | null;
}

const initialState: State = {
  listings: [],
  loading: false,
  loaded: false,
  activeRequestId: null,
  error: null,
};

export const fetchListings = createAsyncThunk(
  'listings/fetch',
  async (params: Prisma.listingsFindManyArgs, { rejectWithValue }) => {
    try {
      const listings = await listingsService.find({ params });
      return listings as listingsWithUploadsAndOpenHouses[];
    } catch (error) {
      return rejectWithValue('Failed to fetch listings');
    }
  }
);

const listingsSlice = createSlice({
  name: 'listings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // A different account signing in next must not inherit this one's data or loaded flag.
      .addCase(logout, () => initialState)
      .addCase(fetchListings.pending, (state, action) => {
        state.activeRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchListings.fulfilled,
        (state, action: PayloadAction<listingsWithUploadsAndOpenHouses[], string, { requestId: string }>) => {
          if (action.meta.requestId !== state.activeRequestId) return;
          state.activeRequestId = null;
          state.loading = false;
          state.loaded = true;
          state.listings = action.payload;
        }
      )
      .addCase(fetchListings.rejected, (state, action) => {
        if (action.meta.requestId !== state.activeRequestId) return;
        state.activeRequestId = null;
        state.loading = false;
        state.error = action.payload as string | null;
      });
  },
});

// eslint-disable-next-line no-empty-pattern
export const {} = listingsSlice.actions;
export default listingsSlice.reducer;
